import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { jwtDecode } from 'jwt-decode';
import authServices from '../services/auth.service';
import forgotPasswordService from '../services/forgot-password.service';
import messageConstant from '../constants/message.constants';
import { ADMIN_EMAIL, SESSION_EXPIRE_TIME } from '../constants/constants';
import { saveUserEncrypted } from '../components/familytree/scripts/aws-dynamo-user';
import { getIconUrl } from '../layouts/global-header/DropdownMenu';

const initialState = {
  user: null,
  loading: false,
  message: '',
  reloadIcon: false,
};

export const login = createAsyncThunk('auth/login', async (payload, { rejectWithValue }) => {
  try {
    const response = await authServices.login(payload);
    return response;
  } catch (error) {
    return rejectWithValue(error);
  }
});

// can be called directly:
const extractGGResponse = async (payload) => {
  // debug-console.log('GG response:', payload);
  const userInfo = jwtDecode(payload.res.credential);
  // // debug-console.log(userInfo);
  userInfo.userId = userInfo.email;
  userInfo.userType = 'GG_USER';
  userInfo.firstTimeLogin = false;
  userInfo.permissions =
    userInfo.email === ADMIN_EMAIL ? ['*', 'DASHBOARD::USER_PROFILE'] : ['USER_PROFILE'];

  const oldUser = JSON.parse(localStorage.getItem('userInfo'));
  if (oldUser?.email !== userInfo.email) {
    if (userInfo.picture) {
      // check if picture exist:
      const iconUrl = getIconUrl(userInfo.userId);

      const res = await fetch(iconUrl);
      // debug-console.log('check Image: res=', res);
      const imageBlob = await res.blob();
      // debug-console.log('check Image: imageBlob=', imageBlob);
      // debug-console.log('extractGG Response: getUserIcon');
      // when file not exist, response will have Text/HTML blob type
      if (imageBlob.type === 'text/html') {
        authServices.getUserIcon(userInfo.picture, userInfo.userId, payload.callback);
      }
    }
    saveUserEncrypted(userInfo);
  }

  userInfo.expireAt = Date.now() + SESSION_EXPIRE_TIME; // 3 hours
  return userInfo;
};

export const getGGResponse = createAsyncThunk(
  'auth/getGGResponse',
  async (payload, { rejectWithValue }) => {
    try {
      return extractGGResponse(payload);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const logout = createAsyncThunk('auth/logout', async () => {
  const response = await authServices.logout();
  return response;
});

export const userNameForgotPasswrod = createAsyncThunk(
  'auth/username',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await forgotPasswordService.getUsername(payload);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const changePassword = createAsyncThunk(
  'auth/changepass',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await forgotPasswordService.changePassword(payload);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const sendOTP = createAsyncThunk('auth/sendOTP', async (payload, { rejectWithValue }) => {
  try {
    const response = await forgotPasswordService.getOTP(payload);
    return response;
  } catch (error) {
    return rejectWithValue(error);
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser(state, action) {
      state.user = action.payload;
    },
    setReloadIcon(state, action) {
      state.reloadIcon = action.payload;
    },
    resetSuccess(state) {
      state.success = false;
    },
    cleanUpState(state) {
      state.message = initialState.message;
      state.loading = initialState.loading;
      state.user = initialState.user;
      state.reloadIcon = initialState.reloadIcon;
      delete state.isCommonMessage;
      delete state.isPwdMessage;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getGGResponse.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getGGResponse.fulfilled, (state, action) => {
      // debug-console.log('+++ set localStorage.userInfo: ', action.payload);
      localStorage.setItem('userInfo', JSON.stringify(action.payload));
      state.user = action.payload;
    });
    builder.addCase(getGGResponse.rejected, (state, action) => {
      // debug-console.log('getGGResponse.rejected:', action.payload);
      state.loading = false;
      state.success = false;
    });
    builder.addCase(login.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(login.fulfilled, (state, action) => {
      if (action.payload.data.data.userType === 'INTERNAL_USER') {
        localStorage.setItem('user', JSON.stringify(action.payload.data.data.token));
        localStorage.setItem('userInfo', JSON.stringify(action.payload.data.data));
        state.loading = false;
        state.user = action.payload.data.data;
      } else {
        state.message = 'IEM051';
        state.isCommonMessage = true;
        state.isPwdMessage = false;
      }
    });
    builder.addCase(login.rejected, (state, action) => {
      const { code, message } = action.payload?.alerts[0];
      if (code === 'IEM002') {
        state.message = message;
        state.isPwdMessage = true;
        state.isCommonMessage = false;
      } else {
        state.message = code;
        state.isCommonMessage = true;
        state.isPwdMessage = false;
      }
      state.loading = false;
      state.success = false;
    });
    builder.addCase(userNameForgotPasswrod.fulfilled, (state, action) => {
      if (action.payload.data?.active === false) {
        state.message = messageConstant.IEM038;
        state.success = false;
      } else if (action.payload.data?.lock === true) {
        state.message = messageConstant.IEM037;
        state.success = false;
      } else {
        state.message = initialState.message;
        state.success = true;
        state.email = action.payload.data.email;
        state.username = action.payload.data.username;
      }
      state.loading = false;
    });
    builder.addCase(userNameForgotPasswrod.rejected, (state, action) => {
      state.message = action.payload.alerts[0].code;
      state.loading = false;
      state.success = false;
    });
    builder.addCase(changePassword.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(changePassword.fulfilled, (state) => {
      state.loading = false;
      state.success = true;
    });
    builder.addCase(changePassword.rejected, (state, action) => {
      const { code } = action.payload.alerts[0];
      if (code === 'IEM004' || code === 'IEM006') {
        state.index = 1;
      } else if (code === 'IEM005') {
        state.index = 2;
      } else {
        state.index = 3;
      }
      state.message = code;
      state.loading = false;
      state.success = false;
    });
  },
});
export const { resetSuccess, cleanUpState, setUser, setReloadIcon } = authSlice.actions;
export default authSlice.reducer;
